import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Stepper, StepsType } from '@veneer/core';
import { primitives } from '@veneer/primitives';

import { Cards, CardsWrapper, Container, CARDS_GAP } from './styles';

export interface CardData {
  key: string | number;
  content: React.ReactNode;
}

export interface CardsCarouselProps {
  cardsData: CardData[];
}

const createInitialSteps = (count: number): StepsType[] =>
  Array.from({ length: count }, (_, index) => ({
    status: index === 0 ? 'complete' : 'incomplete',
    label: `step-${index + 1}`,
  }));

const getActiveCardIndex = (
  container: HTMLDivElement,
  totalCards: number
): number => {
  const scrollLeft = container.scrollLeft;
  const maxScrollLeft = container.scrollWidth - container.clientWidth;
  const scrollPercentage = scrollLeft / maxScrollLeft;
  return Math.min(Math.floor(scrollPercentage * totalCards), totalCards - 1);
};

const updateStepsStatus = (
  steps: StepsType[],
  activeIndex: number
): StepsType[] =>
  steps.map((step, index) => ({
    ...step,
    status: index === activeIndex ? 'complete' : 'incomplete',
  }));

export const CardsCarousel = (cardsCarouselProps: CardsCarouselProps) => {
  const { cardsData } = cardsCarouselProps;

  const [stepItems, setStepItems] = useState<StepsType[]>(() =>
    createInitialSteps(cardsData.length)
  );
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const cardsContainer = cardsRef.current;
    if (!cardsContainer) return;

    const handleScroll = () => {
      if (!cardsRef.current) return;

      const activeIndex = getActiveCardIndex(
        cardsRef.current,
        cardsData.length
      );
      setStepItems((prevSteps) => updateStepsStatus(prevSteps, activeIndex));
    };

    cardsContainer.addEventListener('scroll', handleScroll);
    return () => cardsContainer.removeEventListener('scroll', handleScroll);
  }, []);

  const handleOnChange = useCallback((_: unknown, index = 0) => {
    if (!cardsRef.current) return;

    const cardWidth = cardsRef.current.children[index].clientWidth;
    const scrollPosition = (cardWidth + CARDS_GAP) * index;

    cardsRef.current.scrollTo({
      left: scrollPosition,
      behavior: 'smooth',
    });

    setStepItems((prevSteps) => updateStepsStatus(prevSteps, index));
  }, []);

  const shouldRenderStepper = windowWidth < primitives.layout.breakpoint3;

  return (
    <Container>
      <Cards ref={cardsRef} data-testid="cards-container">
        {cardsData.map((card) => (
          <CardsWrapper key={card.key} data-testid="cards-wrapper">
            <React.Fragment key={card.key}>{card.content}</React.Fragment>
          </CardsWrapper>
        ))}
      </Cards>
      {shouldRenderStepper && (
        <Stepper
          style={{ justifyContent: 'center' }}
          appearance="indicator"
          colorScheme="neutral"
          interactive
          onChange={handleOnChange}
          size={cardsData.length}
          steps={stepItems}
        />
      )}
    </Container>
  );
};
